import React, { useState, useEffect, useRef } from "react";
import Button from "rc-app/components/Button";
import axios from "axios";
import UserService from "rc-app/services/UserService";
import { useLocation, useNavigate } from "react-router-dom";
import { getDigitalTwinById } from "../Home";
import { PropertyCard } from "rc-app/components/Card/propertyCard";
// import { getAgentDefaultCurrency } from "rc-app/api/currencyActionsApi";
import Icon from "rc-app/components/Icons";
// import PaginationRounded from "rc-app/components/Pagination";

export const CustomerDetails = () => {
  const [loading, setLoading] = useState(true);
  const [customerEmailId, setCustomerEmailId] = useState("");
  const [customer, setCustomer] = useState({
    _id: "",
    first_name: "",
    last_name: "",
    email_id: "",
    mobile_number: "",
    profile_pic: "",
    address: {
      address_line1: "",
      address_line2: "",
      landmark: "",
      postal_code: "",
      city: "",
      province: "",
      country: "",
    },
    joined_date: "",
    property_shortlist: [],
  });
  const [digitalTwinDetailList, setDigitalTwinDetailList] = useState([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const customerId = queryParams.get("customerId");
  const [reveal_address] = useState(true);
  const navigate = useNavigate();
  const [deleteModal, setDeleteModal] = useState(false);
  //   const [shortlistPropertyIds, setShortlistPropetyIds] = useState([]);

  // const [currentPage, setCurrentPage] = useState(1);
  //   const [customerList, setCustomerList] = useState([]);
  //   const [totalPages, setTotalPages] = useState(0);
  const [deletePropertyId, setDeletePropertyId] = useState<string | null>(null);

  //   const pageLimit = 10;

  // Refs to prevent multiple calls
  const isFetching = useRef(false);
  const hasFetched = useRef(false);

  // Fetch customer data
  const fetchCustomers = async () => {
    if (!customerId || isFetching.current) return;

    try {
      isFetching.current = true;
      const API_URL = `${process.env.REACT_APP_ONEVERSE_REALESTATE_SERVER_URL}/api/v1/realestate/views/re_customer/${customerId}`;
      const response = await axios.get(API_URL, {
        headers: {
          Authorization: `Bearer ${UserService.getToken()}`,
          "ngrok-skip-browser-warning": "disabled",
          "Content-Type": "application/json",
        },
      });

      setCustomer(response.data.customer);
      setCustomerEmailId(response.data.customer?.email_id || "");

      // Only fetch digital twins if there are properties
      if (response.data.customer.property_shortlist.length > 0) {
        const propertyShortlist = response.data.customer.property_shortlist;
        // const shortlistIds = propertyShortlist.map(
        //   (property: any) => property.mp_digital_twin_id
        // );
        // setShortlistPropetyIds(shortlistIds);

        // Fetch digital twin details
        const promises = propertyShortlist.map((property: any) => {
          const digitalTwinId = property.mp_digital_twin_id;
          return getDigitalTwinById(digitalTwinId, reveal_address);
        });

        const digitalTwinDetails = await Promise.all(promises);
        setDigitalTwinDetailList(digitalTwinDetails);
      } else {
        setDigitalTwinDetailList([]);
        // setShortlistPropetyIds([]);
      }
    } catch (error) {
      console.error("Error fetching Customer details:", error);
    } finally {
      isFetching.current = false;
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      // Prevent multiple calls
      if (!customerId || hasFetched.current) return;

      setLoading(true);
      hasFetched.current = true;

      try {
        // Fetch all required data in parallel where possible
        await Promise.all([fetchCustomers()]);
      } catch (error) {
        console.error("Error fetching initial data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();

    // Cleanup function
    return () => {
      isFetching.current = false;
    };
  }, [customerId]); // Only depend on customerId

  // Function to handle property removal
  const handleRemoveProperty = async () => {
    if (!deletePropertyId || !customerId) return;

    try {
      const API_URL = `${process.env.REACT_APP_ONEVERSE_REALESTATE_SERVER_URL}/api/v1/realestate/actions/agent/remove_property_from_client_property_shortlist/${customerId}?mp_digital_twin_id=${deletePropertyId}`;

      await axios.put(API_URL, null, {
        headers: {
          Authorization: `Bearer ${UserService.getToken()}`,
          "ngrok-skip-browser-warning": "disabled",
          "Content-Type": "application/json",
        },
      });

      // Refresh the customer data after removal
      await fetchCustomers();

      console.log("Property removed successfully");
      setDeleteModal(false);
      setDeletePropertyId(null);
    } catch (error) {
      console.error("Error removing property:", error);
    }
  };

  const handleDetailButton = (
    digital_twin_id: string,
    matterport_id: string
  ) => {
    navigate(
      `/property_details?digitalTwinId=${digital_twin_id}&propertyId=${matterport_id}`,
      {
        state: {
          breadcrumbs: [
            { path: "/", label: "Home" },
            { path: "/customers", label: "My Customers" },
            {
              path: `/customer_details?customerId=${customerId}`,
              label: "Customer Details",
            },
            {
              path: `/property_details?digitalTwinId=${digital_twin_id}&propertyId=${matterport_id}`,
              label: "Projects Details",
            },
          ],
          referrer: `/customer_details?customerId=${customerId}`,
        },
      }
    );
  };

  const formatDate = (dateString: any) => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid Date";

    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "short" });
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");

    const ampm = hours >= 12 ? "pm" : "am";
    hours = hours % 12 || 12;

    return `${day}-${month}-${year}, ${hours}:${minutes}${ampm}`;
  };

  const handleAddShortlist = () => {
    navigate(`/add_shortlist?customerId=${customerId}`, {
      state: {
        c_email: customerEmailId,
        breadcrumbs: [
          { path: "/", label: "Home" },
          { path: "/customers", label: "My Customers" },
          {
            path: `/customer_details?customerId=${customerId}`,
            label: "Customer Details",
          },
          {
            path: `/add_shortlist?customerId=${customerId}`,
            label: "Add Shortlist Property",
          },
        ],
        referrer: `/customer_details?customerId=${customerId}`,
      },
    });
  };

  const openDeleteModal = (propertyId: string) => {
    setDeletePropertyId(propertyId);
    setDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setDeleteModal(false);
    setDeletePropertyId(null);
  };

  useEffect(() => {
    if (!customerId) {
      navigate("/customers", { replace: true });
      return;
    }

    // If customerId exists but no breadcrumbs state (page reload), restore breadcrumbs
    if (!location.state?.breadcrumbs) {
      navigate(`/customer_details?customerId=${customerId}`, {
        replace: true,
        state: {
          breadcrumbs: [
            { path: "/", label: "Home" },
            { path: "/customers", label: "My Customers" },
            {
              path: `/customer_details?customerId=${customerId}`,
              label: "Customer Details",
            },
          ],
        },
      });
    }
  }, [customerId, location.state, navigate]);

  return (
    <>
      <div className="flex w-full flex-col gap-3">
        {loading ? (
          <div className="flex min-h-screen justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            <div className="grid gap-3 max-md:grid-cols-1">
              <div
                className="flex max-lg:w-full 
                  md:rounded-2xl 
                  lg:w-fit md:max-w-none md:mx-auto
                  md:px-3 lg:px-6 
                  bg-[#1F3A4A] text-white 
                  px-1 py-4 shadow-lg 
                  items-center justify-center gap-4
                "
              >
                {/* IMAGE BLOCK */}
                <div className="flex max-md:w-[35%] md:w-auto items-center justify-center">
                  {customer?.profile_pic ? (
                    <div className="w-28 h-28 md:w-32 md:h-32  rounded-full overflow-hidden border-4 border-[#889DB2] shadow-md">
                      {/* <img
                        src={customer?.profile_pic}
                        alt="Profile"
                        className=""
                      /> */}
                      <img
                        src={customer?.profile_pic}
                        alt="Profile"
                        className="w-full h-full object-fill
                      "
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    </div>
                  ) : (
                    <div
                      className="
                        w-28 h-28 md:w-36 md:h-36 rounded-full 
                        flex items-center justify-center 
                        bg-[#2E4051] text-[#889DB2] 
                        text-3xl md:text-5xl font-bold 
                        border-4 border-[#889DB2] shadow-md
                      "
                    >
                      {`${customer?.first_name?.[0]?.toUpperCase()}${customer?.last_name?.[0]?.toUpperCase()}` ||
                        ""}
                    </div>
                  )}
                </div>

                {/* RIGHT SIDE CONTENT */}
                <div className="flex flex-col md:gap-6 gap-4 w-full">
                  {/* NAME */}
                  <div className="flex w-full items-center gap-2">
                    <h2 className="text-xl md:text-2xl font-semibold">
                      {`${customer?.first_name || ""} ${
                        customer?.last_name || ""
                      }`}
                    </h2>
                  </div>

                  {/* DETAILS ROWS FOR WEB */}
                  <div className="flex flex-col md:flex-row gap-3 md:gap-4 w-full">
                    {/* Join Date */}
                    <div className="flex max-md:flex-row flex-col gap-2">
                      <div className="flex gap-2">
                        <Icon name="ic-handshake" className="w-5 h-5 " />
                        <span className="max-md:hidden text-[#889DB2]  dark:text-[#889DB2] text-sm flex items-center ">
                          Onboarded On
                        </span>
                      </div>
                      <span className="text-black dark:text-white text-sm">
                        {formatDate(customer.joined_date)}
                      </span>
                    </div>

                    {/* Properties */}
                    <div className="flex max-md:flex-row flex-col  gap-2">
                      <div className="flex gap-2">
                        <Icon name="ic-properties" className="w-5 h-5" />
                        <span className="max-md:hidden text-[#889DB2]  dark:text-[#889DB2] text-sm flex items-center ">
                          Shortlisted
                        </span>
                      </div>
                      <span className="text-black dark:text-white text-sm">
                        {`${
                          customer?.property_shortlist?.length || 0
                        } Properties`}
                      </span>
                    </div>

                    {/* Email */}
                    <div className="flex max-md:flex-row flex-col gap-2">
                      <div className="flex gap-2">
                        <Icon name="ic-mail" className="w-5 h-5" />
                        <span className="max-md:hidden text-[#889DB2]  dark:text-[#889DB2] text-sm flex items-center ">
                          Email ID
                        </span>
                      </div>
                      <span className="text-black dark:text-white text-sm">
                        {customer?.email_id || "N/A"}
                      </span>
                    </div>

                    {/* Phone */}
                    <div className="flex max-md:flex-row flex-col gap-2">
                      <div className="flex gap-2">
                        <Icon name="ic-call" className="w-5 h-5" />
                        <span className="max-md:hidden text-[#889DB2]  dark:text-[#889DB2] text-sm flex items-center ">
                          Mobile Number
                        </span>
                      </div>
                      <span className="text-black dark:text-white text-sm">
                        {customer?.mobile_number || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex w-full px-2 flex-col md:flex-row md:justify-between md:items-center gap-2">
              <h2 className="text-xl font-semibold text-[#F4F5F7]">{`Shortlisted Properties (${
                customer?.property_shortlist?.length || 0
              })`}</h2>
              <div className="flex max-md:order-1 bg-[#2E4051] items-center justify-center rounded-md max-md:w-full w-80 shadow-md cursor-pointer">
                <Button
                  children={`Add a new property to Shortlist`}
                  className="flex w-full bg-[#008564] rounded-md items-center justify-center text-[20px] md:px-4 h-[40px] drop-shadow-[0_2px_2px_rgba(0,0,0,0.25)]"
                  onClick={handleAddShortlist}
                />
              </div>
            </div>

            {digitalTwinDetailList.length > 0 ? (
              <div className="grid gap-3 grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 px-2">
                {digitalTwinDetailList.map((property: any) => (
                  <div
                    key={property._id}
                    className="relative grid grid-cols-1 pt-2 pb-2.5 px-2 gap-4 rounded-md bg-white dark:bg-[#0C1117] border-[#374B5E] cursor-pointer hover:shadow-[0_45px_75px_-18px_rgba(0,0,0,0.5)] hover:-translate-y-1 transition-all duration-400"
                  >
                    <div
                      className=""
                      onClick={() =>
                        handleDetailButton(property._id, property.matterport_id)
                      }
                    >
                      {/* Thumbnail Section */}
                      <div className="flex flex-col gap-4">
                        <div className="relative aspect-video rounded-md overflow-hidden bg-gray-100">
                          <img
                            src={property.property_thumbnail || ""}
                            alt="Property thumbnail"
                            className="object-cover w-full max-h-full"
                            loading="lazy"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display =
                                "none";
                            }}
                          />

                          {/* Badge */}
                          <span
                            className={`flex w-20 absolute right-0 top-0 py-1 px-2 justify-center rounded text-[11px] max-sm:text-[8px] font-semibold
                              ${
                                property?.property_visibility === "exclusive"
                                  ? "bg-gradient-to-r from-[#FFC248] via-[#FFE8BB] to-[#FFC248] text-[#664400]"
                                  : "bg-[linear-gradient(90deg,#209F26,#00D5F6)] text-[#002904]"
                              }
                            `}
                          >
                            {property?.property_visibility === "exclusive"
                              ? "EXCLUSIVE"
                              : "NETWORK"}
                          </span>
                        </div>
                      </div>
                      {/* Details Section */}
                      <div className="flex flex-col justify-between gap-2">
                        <PropertyCard
                          digitalTwin={property}
                          isExclusive={false}
                          amount={property?.pricing}
                          contractType={property.characteristics.contract_type}
                          tours={property.characteristics.total_virtual_tours}
                          isCustomerDetails={false}
                          isDetails={false}
                        />
                      </div>
                    </div>

                    {/* REMOVE BUTTON */}
                    <div
                      className="flex absolute bottom-1 right-1 bg-[#ED5562] w-9 h-9 rounded-md items-center justify-center cursor-pointer hover:bg-[#d94451]"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDeleteModal(property._id);
                      }}
                    >
                      <Icon
                        name="ic-delete"
                        className="w-5 h-5 text-white cursor-pointer"
                        style={{ filter: "brightness(0) invert(1)" }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <div className="text-gray-400 mb-4">
                  <Icon name="ic-properties" className="w-16 h-16" />
                </div>
                <h3 className="text-xl font-semibold text-gray-300 mb-2">
                  No Shortlisted Properties
                </h3>
                <p className="text-gray-400 text-center">
                  This customer hasn't shortlisted any properties yet.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* {totalPages > 1 && (
        <div className="m-5">
          <PaginationRounded
            currentPage={currentPage}
            totalPages={totalPages}
            limit={pageLimit}
            onPageChange={handlePageChange}
          />
        </div>
      )} */}

      {/* Delete Modal */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50">
          <div className="bg-[#0D1B2A] text-white w-[90%] max-w-sm rounded-xl p-6 relative shadow-xl">
            {/* Close button */}
            <button
              onClick={closeDeleteModal}
              className="absolute right-4 top-4 text-gray-300 hover:text-white"
            >
              <Icon
                name="ic-close"
                className="w-5 h-5 text-white cursor-pointer"
                style={{ filter: "brightness(0) invert(1)" }}
              />
            </button>

            {/* Delete Icon */}
            <div className="flex justify-center mb-4">
              <div className="bg-[#ED5562] p-3 rounded-full">
                <Icon
                  name="ic-delete"
                  className="w-5 h-5 text-white cursor-pointer"
                  style={{ filter: "brightness(0) invert(1)" }}
                />
              </div>
            </div>

            {/* Heading */}
            <h2 className="text-center text-xl font-semibold">
              Remove Property?
            </h2>

            {/* Subtitle */}
            <p className="text-center text-gray-300 mt-2 mb-6 px-2">
              Are you sure you want to remove this property from the shortlist?
            </p>

            {/* Buttons */}
            <div className="flex justify-between gap-3">
              <button
                onClick={closeDeleteModal}
                className="w-1/2 py-2 rounded-lg bg-gray-600 hover:bg-gray-500 text-white font-medium"
              >
                Cancel
              </button>

              <button
                className="w-1/2 py-2 rounded-lg bg-[#ED5562] hover:bg-red-500 text-white font-medium"
                onClick={handleRemoveProperty}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
